<template>
	<div class="container upgrade-launch">
		<JuniperPageHeader
			:back="backTo"
			:title="diff ? diff.live.name : ''"
			subtitle="Upgrade Launch — review changes before going live"
		>
			<template #title-actions>
				<span class="ul-status-pill">
					<font-awesome-icon :icon="['fas', 'sync']" class="me-1" /> Upgrade
				</span>
			</template>
		</JuniperPageHeader>

		<div v-if="loading" class="text-center py-5">
			<span class="spinner-border" role="status"></span>
			<div class="text-muted mt-2">Computing differences…</div>
		</div>

		<div v-else-if="error" class="alert alert-danger">
			<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
			{{ error }}
		</div>

		<template v-else-if="diff">
			<!-- Live vs Upgrade identity -->
			<div class="ul-identity">
				<div class="ul-identity__col ul-identity__col--live">
					<span class="ul-badge ul-badge--live">LIVE</span>
					<div class="ul-identity__name">{{ diff.live.name }}</div>
					<a v-if="diff.live.url" :href="ensureUrl(diff.live.url)" target="_blank" class="ul-identity__url">
						{{ stripProtocol(diff.live.url) }}
					</a>
				</div>
				<div class="ul-identity__arrow">
					<font-awesome-icon :icon="['fas', 'arrow-right']" />
				</div>
				<div class="ul-identity__col ul-identity__col--upgrade">
					<span class="ul-badge ul-badge--upgrade">UPGRADE</span>
					<div class="ul-identity__name">{{ diff.upgrade.name }}</div>
					<a v-if="diff.upgrade.staging_url" :href="ensureUrl(diff.upgrade.staging_url)" target="_blank" class="ul-identity__url">
						{{ stripProtocol(diff.upgrade.staging_url) }}
					</a>
				</div>
			</div>

			<!-- Summary chips -->
			<div class="ul-chips">
				<div class="ul-chip ul-chip--added">
					<span class="ul-chip__value">{{ diff.summary.added }}</span>
					<span class="ul-chip__label">Added</span>
				</div>
				<div class="ul-chip ul-chip--removed">
					<span class="ul-chip__value">{{ diff.summary.removed }}</span>
					<span class="ul-chip__label">Removed</span>
				</div>
				<div class="ul-chip ul-chip--modified">
					<span class="ul-chip__value">{{ diff.summary.modified }}</span>
					<span class="ul-chip__label">Modified</span>
				</div>
			</div>

			<!-- Theme + Settings -->
			<div class="ul-panel">
				<h6 class="ul-panel__title">Theme &amp; Homepage Settings</h6>
				<p v-if="diff.homepage_settings.changed" class="ul-section-note">
					On apply, the live settings are replaced by the upgrade's settings — this is how the active theme and homepage configuration actually go live.
				</p>
				<div class="ul-row ul-row--head">
					<div class="ul-row__cell">Setting</div>
					<div class="ul-row__cell">Live</div>
					<div class="ul-row__cell">Upgrade</div>
				</div>
				<template v-for="opt in diff.homepage_settings.rows">
					<!-- short scalar value: keep inline -->
					<div
						v-if="!isComplexOpt(opt)"
						:key="opt.key"
						class="ul-row"
						:class="`ul-row--${opt.status}`"
					>
						<div class="ul-row__cell ul-row__key">
							{{ opt.key }}
							<span class="ul-tag" :class="`ul-tag--${opt.status}`">{{ opt.status }}</span>
						</div>
						<div class="ul-row__cell ul-row__val">{{ truncate(opt.live, 80) }}</div>
						<div class="ul-row__cell ul-row__val">{{ truncate(opt.upgrade, 80) }}</div>
					</div>

					<!-- large / JSON value: collapse behind a disclosure -->
					<div
						v-else
						:key="opt.key"
						class="ul-row ul-row--full"
						:class="`ul-row--${opt.status}`"
					>
						<button class="ul-disclosure" type="button" @click="toggle(opt.key)">
							<font-awesome-icon :icon="['fas', expanded[opt.key] ? 'chevron-down' : 'chevron-right']" />
							<span class="ul-row__key">{{ opt.key }}</span>
							<span class="ul-tag" :class="`ul-tag--${opt.status}`">{{ opt.status }}</span>
							<span class="ul-disclosure__hint">{{ expanded[opt.key] ? 'hide' : 'view diff' }}</span>
						</button>
						<div v-if="expanded[opt.key]" class="ul-jsondiff">
							<div class="ul-jsondiff__col">
								<div class="ul-jsondiff__label">Live</div>
								<pre class="ul-pre">{{ prettyJson(opt.live) }}</pre>
							</div>
							<div class="ul-jsondiff__col">
								<div class="ul-jsondiff__label">Upgrade</div>
								<pre class="ul-pre">{{ prettyJson(opt.upgrade) }}</pre>
							</div>
						</div>
					</div>
				</template>
				<div v-if="!diff.homepage_settings.changed" class="ul-empty">
					No settings changes.
				</div>
			</div>

			<!-- HeyGov synced -->
			<div class="ul-synced">
				<font-awesome-icon :icon="['fas', 'sync']" class="me-2 text-info" />
				<span>
					<strong>{{ syncedLabels }}</strong> are synchronized from HeyGov (same jurisdiction on live and upgrade) — not part of the upgrade.
				</span>
			</div>

			<!-- Content sections -->
			<div v-for="(section, name) in changedSections" :key="name" class="ul-panel">
				<h6 class="ul-panel__title">
					{{ section.label }}
					<span class="ul-counts">
						<span v-if="section.counts.added" class="ul-tag ul-tag--added">+{{ section.counts.added }}</span>
						<span v-if="section.counts.removed" class="ul-tag ul-tag--removed">−{{ section.counts.removed }}</span>
						<span v-if="section.counts.modified" class="ul-tag ul-tag--modified">~{{ section.counts.modified }}</span>
						<span class="ul-counts__unchanged">{{ section.unchanged_count }} unchanged</span>
					</span>
				</h6>
				<p class="ul-section-note">{{ sectionSummary(section) }}</p>
				<div class="ul-row ul-row--head">
					<div class="ul-row__cell">Live</div>
					<div class="ul-row__cell ul-row__statuscol"></div>
					<div class="ul-row__cell">Upgrade</div>
				</div>
				<div v-for="(row, i) in sectionRows(section)" :key="name + '-' + i" class="ul-row" :class="`ul-row--${row.status}`">
					<div class="ul-row__cell ul-row__val">
						<template v-if="row.live">{{ row.live.label || row.live.key }}</template>
						<span v-else class="ul-row__placeholder">—</span>
					</div>
					<div class="ul-row__cell ul-row__statuscol">
						<span class="ul-tag" :class="`ul-tag--${row.status}`">{{ row.status }}</span>
					</div>
					<div class="ul-row__cell ul-row__val">
						<template v-if="row.upgrade">{{ row.upgrade.label || row.upgrade.key }}</template>
						<span v-else class="ul-row__placeholder">—</span>
					</div>
				</div>
			</div>

			<div v-if="!hasAnyChange" class="ul-empty ul-empty--big">
				<font-awesome-icon :icon="['fas', 'check-circle']" class="text-success me-2" />
				No differences detected between live and upgrade.
			</div>

			<!-- Action bar -->
			<div class="ul-actions">
				<button class="j-action-btn j-action-btn--danger ul-action-btn" :disabled="busy || apply.active" @click="confirmDiscard">
					<font-awesome-icon :icon="['fas', 'trash']" class="me-2" /> Discard Upgrade
				</button>
				<button class="j-action-btn j-action-btn--success ul-action-btn" :disabled="busy || apply.active" @click="confirmApply">
					<font-awesome-icon :icon="['fas', 'rocket']" class="me-2" />
					Apply to Live
				</button>
			</div>
		</template>

		<!-- Apply: confirm → progress, in one modal -->
		<div v-if="apply.active" class="ul-modal-backdrop">
			<div class="ul-modal">
				<!-- STEP 1 — confirm -->
				<template v-if="apply.phase === 'confirm'">
					<div class="ul-modal__icon ul-modal__icon--warn">
						<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
					</div>
					<h5 class="ul-modal__title">Apply upgrade to the live client?</h5>
					<p class="ul-modal__msg">
						The upgrade content will <strong>replace</strong> the current live content.
						The current live content is archived first and can be rolled back.
					</p>
					<div class="ul-modal__actions">
						<button class="j-action-btn ul-action-btn" @click="closeApply">Cancel</button>
						<button class="j-action-btn j-action-btn--success ul-action-btn" @click="runApply">
							<font-awesome-icon :icon="['fas', 'rocket']" class="me-2" /> Yes, go live
						</button>
					</div>
				</template>

				<!-- STEP 2 — progress / result -->
				<template v-else>
					<div v-if="apply.failed" class="ul-modal__icon ul-modal__icon--warn">
						<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
					</div>
					<div v-else-if="apply.done" class="ul-modal__icon ul-modal__icon--ok">
						<font-awesome-icon :icon="['fas', 'check-circle']" />
					</div>
					<div v-else class="ul-modal__icon">
						<span class="spinner-border text-success" role="status"></span>
					</div>

					<h5 class="ul-modal__title">
						{{ apply.done ? 'Upgrade is live' : (apply.failed ? 'Media relocation failed' : 'Going live…') }}
					</h5>
					<p class="ul-modal__msg">{{ apply.message }}</p>

					<template v-if="apply.total > 0 && !apply.done && !apply.failed">
						<div class="ul-progress"><div class="ul-progress__bar" :style="{ width: progressPct + '%' }"></div></div>
						<p class="ul-progress__count">{{ apply.copied }} / {{ apply.total }} files</p>
					</template>

					<p v-if="apply.failed" class="ul-modal__hint">
						The upgrade itself was applied successfully — only media relocation failed. It retries automatically; the site still works.
					</p>

					<button
						v-if="apply.done || apply.failed || apply.stalled"
						class="j-action-btn j-action-btn--primary ul-action-btn ul-modal__btn"
						@click="goToLive"
					>
						Go to live client
					</button>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'
import JuniperPageHeader from '@/components/juniper/layout/JuniperPageHeader'

export default {
	name: 'ClientUpgradeLaunch',
	components: { JuniperPageHeader },
	data() {
		return {
			loading: true,
			busy: false,
			error: '',
			diff: null,
			expanded: {},
			apply: { active: false, done: false, failed: false, stalled: false, phase: '', message: '', copied: 0, total: 0, timer: null },
		}
	},
	beforeDestroy() {
		this.stopApplyPolling()
	},
	computed: {
		clientId() {
			return this.$route.params.id
		},
		// Back returns to the upgrade copy (where the user came from), not the live client.
		backTo() {
			const upgradeId = this.diff?.upgrade?.id
			return upgradeId
				? `/juniper/clients/edit/${upgradeId}`
				: `/juniper/clients/edit/${this.clientId}`
		},
		changedSections() {
			if (!this.diff) return {}
			const out = {}
			for (const [name, s] of Object.entries(this.diff.sections)) {
				if (s.counts.added || s.counts.removed || s.counts.modified) {
					out[name] = s
				}
			}
			return out
		},
		hasAnyChange() {
			if (!this.diff) return false
			const s = this.diff.summary
			return s.added || s.removed || s.modified || this.diff.homepage_settings.changed
		},
		syncedLabels() {
			if (!this.diff) return ''
			return this.diff.synced.map(s => s.label).join(' & ')
		},
		progressPct() {
			return this.apply.total > 0 ? Math.round((this.apply.copied / this.apply.total) * 100) : 0
		},
	},
	created() {
		this.fetchDiff()
	},
	methods: {
		async fetchDiff() {
			this.loading = true
			this.error = ''
			try {
				const { data } = await juniperApi.get(`/clients/${this.clientId}/upgrade/diff`)
				this.diff = data
			} catch (err) {
				this.error = err?.response?.data?.message ?? err.message ?? 'Failed to load diff.'
			} finally {
				this.loading = false
			}
		},

		toggle(key) {
			this.$set(this.expanded, key, !this.expanded[key])
		},
		isComplexVal(v) {
			if (v === null || v === undefined) return false
			const s = String(v).trim()
			return s.length > 80 || s.startsWith('{') || s.startsWith('[')
		},
		isComplexOpt(opt) {
			return this.isComplexVal(opt.live) || this.isComplexVal(opt.upgrade)
		},
		prettyJson(v) {
			if (v === null || v === undefined || v === '') return '—'
			try {
				return JSON.stringify(JSON.parse(v), null, 2)
			} catch (e) {
				return String(v)
			}
		},

		// Human sentence describing the post-apply outcome for a section.
		// After apply the live count equals the upgrade count exactly
		// (live content is replaced by the upgrade's content).
		sectionSummary(section) {
			const c = section.counts
			const label = section.label.toLowerCase()
			const parts = []
			if (c.added) parts.push(`${c.added} added`)
			if (c.removed) parts.push(`${c.removed} removed`)
			if (c.modified) parts.push(`${c.modified} updated`)
			const changes = parts.length ? parts.join(', ') : 'no changes'
			return `${changes}. Live has ${c.live} ${label} now → ${c.upgrade} after going live`
				+ ` (${section.unchanged_count} untouched stay, removed ones are deleted from live).`
		},

		sectionRows(section) {
			const rows = []
			section.modified.forEach(i => rows.push({ status: 'modified', live: i, upgrade: i }))
			section.added.forEach(i => rows.push({ status: 'added', live: null, upgrade: i }))
			section.removed.forEach(i => rows.push({ status: 'removed', live: i, upgrade: null }))
			return rows
		},

		// Opens the modal on the confirm step; the work starts from runApply().
		confirmApply() {
			this.apply.active = true
			this.apply.done = false
			this.apply.failed = false
			this.apply.stalled = false
			this.apply.phase = 'confirm'
			this.apply.message = ''
			this.apply.copied = 0
			this.apply.total = 0
		},

		closeApply() {
			this.stopApplyPolling()
			this.apply.active = false
			this.apply.phase = ''
		},

		async runApply() {
			this.apply.phase = 'applying'
			this.apply.message = 'Applying upgrade to the live client…'
			this.startApplyPolling()

			try {
				// The apply itself is synchronous; media relocation then runs in
				// the background. Polling drives the modal through both phases.
				await juniperApi.post(`/clients/${this.clientId}/upgrade/apply`)
			} catch (err) {
				this.stopApplyPolling()
				this.apply.active = false
				this.apply.phase = ''
				this.$swal.fire({
					icon: 'error',
					title: 'Apply failed.',
					text: err?.response?.data?.error ?? err?.response?.data?.message ?? err.message,
					showConfirmButton: true,
				})
			}
		},

		startApplyPolling() {
			this.stopApplyPolling()
			let elapsed = 0
			this.apply.timer = setInterval(async () => {
				elapsed += 1500
				try {
					const { data } = await juniperApi.get(`/clients/${this.clientId}/upgrade/relocation-status`)
					if (data && data.phase) {
						this.apply.phase = data.phase
						this.apply.message = data.message || this.apply.message
						this.apply.copied = data.copied || 0
						this.apply.total = data.total || 0
						if (data.phase === 'done') {
							this.apply.done = true
							this.stopApplyPolling()
						} else if (data.phase === 'failed') {
							this.apply.failed = true
							this.stopApplyPolling()
						}
					}
				} catch (e) {
					// transient — keep polling
				}
				if (elapsed > 600000) {
					this.apply.stalled = true
					this.apply.message = 'Media relocation is still running in the background — you can leave this page.'
					this.stopApplyPolling()
				}
			}, 1500)
		},

		stopApplyPolling() {
			if (this.apply.timer) {
				clearInterval(this.apply.timer)
				this.apply.timer = null
			}
		},

		goToLive() {
			this.$router.push(`/juniper/clients/edit/${this.clientId}`).catch(() => {})
		},

		async confirmDiscard() {
			const confirm = await this.$swal.fire({
				icon: 'warning',
				title: 'Discard the upgrade copy?',
				text: 'The upgrade content will be permanently deleted. The live client is untouched.',
				showCancelButton: true,
				showConfirmButton: true,
				confirmButtonText: 'Yes, discard',
				cancelButtonText: 'Cancel',
				confirmButtonColor: '#dc3545',
			})
			if (!confirm.isConfirmed) return

			this.busy = true
			try {
				await juniperApi.delete(`/clients/${this.clientId}/upgrade`)
				await this.$swal.fire({ icon: 'success', title: 'Upgrade discarded.', timer: 2500, showConfirmButton: false })
				this.$router.push(`/juniper/clients/edit/${this.clientId}`).catch(() => {})
			} catch (err) {
				this.$swal.fire({
					icon: 'error',
					title: 'Discard failed.',
						showConfirmButton: true,
					text: err?.response?.data?.error ?? err?.response?.data?.message ?? err.message,
				})
			} finally {
				this.busy = false
			}
		},

		stripProtocol(url) {
			return url ? url.replace(/^https?:\/\//, '') : ''
		},
		ensureUrl(url) {
			if (!url) return '#'
			return /^https?:\/\//.test(url) ? url : `https://${url}`
		},
		truncate(val, len = 60) {
			if (val === null || val === undefined || val === '') return '—'
			const s = String(val)
			return s.length > len ? s.slice(0, len) + '…' : s
		},
	},
}
</script>

<style scoped>
.upgrade-launch {
	padding-bottom: 60px;
}

/* Status pill in the page header (matches the Live pill on client pages) */
.ul-status-pill {
	display: inline-flex;
	align-items: center;
	background: #ede9fe;
	color: #6d28d9;
	font-weight: 700;
	font-size: 13px;
	border-radius: 999px;
	padding: 6px 16px;
}

/* Per-section "what happens on apply" note */
.ul-section-note {
	font-size: 12px;
	color: #6b7280;
	margin: -2px 0 10px;
}

/* Identity */
.ul-identity {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 12px;
	background: #fff;
	border: 1px solid #eef0f2;
	border-radius: 14px;
	padding: 16px 20px;
	margin-bottom: 16px;
}
.ul-identity__col { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.ul-identity__col--upgrade { text-align: right; align-items: flex-end; }
.ul-identity__name { font-weight: 600; color: #111827; }
.ul-identity__url { font-size: 13px; color: #1d4ed8; text-decoration: none; }
.ul-identity__url:hover { text-decoration: underline; }
.ul-identity__arrow { color: #9ca3af; font-size: 18px; }
.ul-badge {
	font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
	border-radius: 6px; padding: 2px 8px; width: fit-content;
}
.ul-badge--live { background: #dbeafe; color: #1d4ed8; }
.ul-badge--upgrade { background: #dcfce7; color: #166534; }

/* Chips */
.ul-chips { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.ul-chip {
	flex: 1; min-width: 110px; background: #fff; border: 1px solid #eef0f2;
	border-radius: 12px; padding: 12px 16px; display: flex; flex-direction: column;
}
.ul-chip__value { font-size: 22px; font-weight: 700; line-height: 1; }
.ul-chip__label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; margin-top: 4px; }
.ul-chip--added .ul-chip__value { color: #16a34a; }
.ul-chip--removed .ul-chip__value { color: #dc2626; }
.ul-chip--modified .ul-chip__value { color: #d97706; }
.ul-chip--theme .ul-chip__value { color: #6b7280; }
.ul-chip--theme.ul-chip--changed .ul-chip__value { color: #7c3aed; }

/* Synced note */
.ul-synced {
	display: flex; align-items: center; background: #eff6ff; border: 1px solid #bfdbfe;
	color: #1e40af; border-radius: 10px; padding: 10px 14px; font-size: 13px; margin-bottom: 16px;
}

/* Panels */
.ul-panel {
	background: #fff; border: 1px solid #eef0f2; border-radius: 14px;
	padding: 14px 18px; margin-bottom: 16px;
}
.ul-panel__title {
	font-weight: 700; font-size: 14px; margin: 0 0 10px;
	display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.ul-counts { display: flex; align-items: center; gap: 6px; font-weight: 400; }
.ul-counts__unchanged { font-size: 11px; color: #9ca3af; }

.ul-row {
	display: grid; grid-template-columns: 1fr auto 1fr; gap: 12px;
	align-items: center; padding: 7px 0; border-bottom: 1px solid #f3f4f6;
}
.ul-row:last-child { border-bottom: none; }
.ul-row--head { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #9ca3af; font-weight: 600; }
.ul-row__cell { min-width: 0; overflow-wrap: anywhere; }
.ul-row__statuscol { text-align: center; }
.ul-row__key { font-weight: 600; color: #374151; font-size: 13px; }
.ul-row__val { font-size: 13px; color: #374151; }
.ul-row__placeholder { color: #d1d5db; }
.ul-row--modified { background: #fffbeb; }
.ul-row--added { background: #f0fdf4; }
.ul-row--removed { background: #fef2f2; }
.ul-row--modified, .ul-row--added, .ul-row--removed { border-radius: 6px; padding-left: 8px; padding-right: 8px; }

.ul-tag {
	font-size: 10px; font-weight: 700; border-radius: 5px; padding: 1px 6px;
	text-transform: uppercase; letter-spacing: 0.03em; margin-left: 6px;
}
.ul-tag--added { background: #dcfce7; color: #15803d; }
.ul-tag--removed { background: #fee2e2; color: #b91c1c; }
.ul-tag--modified { background: #fef3c7; color: #b45309; }

/* Collapsible JSON/large setting rows */
.ul-row--full { grid-template-columns: 1fr; display: block; }
.ul-disclosure {
	display: flex; align-items: center; gap: 8px; width: 100%;
	background: none; border: none; padding: 2px 0; cursor: pointer;
	font: inherit; color: #374151; text-align: left;
}
.ul-disclosure > svg { color: #9ca3af; font-size: 11px; }
.ul-disclosure__hint { margin-left: auto; font-size: 11px; color: #9ca3af; }
.ul-jsondiff { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 8px; }
.ul-jsondiff__label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #9ca3af; margin-bottom: 4px; }
.ul-pre {
	background: #f9fafb; border: 1px solid #eef0f2; border-radius: 8px;
	padding: 8px 10px; font-size: 11px; line-height: 1.45; margin: 0;
	max-height: 280px; overflow: auto; white-space: pre-wrap; word-break: break-word;
}
@media (max-width: 640px) {
	.ul-jsondiff { grid-template-columns: 1fr; }
}

.ul-empty { color: #9ca3af; font-size: 13px; padding: 6px 0; }
.ul-empty--big { text-align: center; padding: 30px; font-size: 15px; background: #fff; border: 1px solid #eef0f2; border-radius: 14px; }

/* Actions */
.ul-actions {
	position: sticky; bottom: 0; display: flex; justify-content: space-between;
	gap: 12px; background: rgba(255, 255, 255, 0.92); backdrop-filter: blur(4px);
	border-top: 1px solid #e5e7eb; padding: 16px 20px; margin-top: 8px;
}
/* Header-style buttons, a touch bigger for this final step */
.ul-actions .ul-action-btn {
	height: 42px;
	font-size: 15px;
	font-weight: 600;
	padding: 0 22px;
	border-radius: 22px;
}

@media (max-width: 640px) {
	.ul-identity { grid-template-columns: 1fr; }
	.ul-identity__col--upgrade { text-align: left; align-items: flex-start; }
	.ul-identity__arrow { transform: rotate(90deg); }
}

/* Apply / relocation progress modal */
.ul-modal-backdrop {
	position: fixed; inset: 0; z-index: 1080;
	background: rgba(15, 23, 42, 0.45);
	display: flex; align-items: center; justify-content: center; padding: 20px;
}
.ul-modal {
	background: #fff; border-radius: 16px; padding: 28px 26px; width: 100%; max-width: 420px;
	text-align: center; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
.ul-modal__icon { font-size: 34px; margin-bottom: 10px; }
.ul-modal__icon--ok { color: #16a34a; }
.ul-modal__icon--warn { color: #d97706; }
.ul-modal__title { font-weight: 700; margin: 0 0 4px; }
.ul-modal__msg { color: #4b5563; font-size: 14px; margin: 0 0 14px; }
.ul-modal__hint { color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 8px 10px; font-size: 12px; margin: 0 0 14px; }
.ul-modal__btn { margin-top: 4px; }
.ul-modal__actions { display: flex; justify-content: center; gap: 10px; margin-top: 4px; }
.ul-progress { height: 8px; background: #e5e7eb; border-radius: 999px; overflow: hidden; }
.ul-progress__bar { height: 100%; background: #16a34a; border-radius: 999px; transition: width 0.3s ease; }
.ul-progress__count { font-size: 12px; color: #6b7280; margin: 6px 0 0; }
</style>
